import React, { useState, useEffect, useContext, useCallback } from 'react';
import Context from "../auth/Context";
import { nodeTypeMetadata as staticNodeTypeMetadata } from "./nodes"; 

// --- MUI Components ---
import { 
    Box, 
    Typography, 
    Button, 
    IconButton, 
    Stack, 
    Card, 
    CardContent, 
    Divider, 
    Alert 
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';

// --- Custom Fields ---
import TextField from '../TextField';
import NumberField from '../NumberField';
import Picker from '../Picker';
import AutoCompleteInput from '../AutoCompleteInput';
import Checkbox from '../Checkbox';
import CodeEditor from '../CodeEditor';
import FormBase from '../FormBaseSteps';
import { getItemsForPicker, getItemsForCompoleteInput } from "../../utils";

/**
 * Những kiểu trường mà form này VẼ ĐƯỢC. Trường có kiểu ngoài danh sách (hoặc thiếu dữ liệu bắt buộc
 * để vẽ, ví dụ 'select' mà không có options) sẽ rơi vào ô nhập dự phòng bên dưới.
 * Cần thiết vì metadata nay có thể đến từ MÁY CHỦ (node động, node của gói mở rộng): máy chủ có thể
 * khai một kiểu client chưa hỗ trợ — trước đây trường đó biến mất không dấu vết, người dùng lưu node
 * thiếu dữ liệu mà không hiểu vì sao.
 */
const isRenderableField = (field) => {
    if (!field || !field.type) return false;
    switch (field.type) {
        case 'text': case 'password': case 'textarea': case 'code':
        case 'number': case 'checkbox': case 'list':
            return true;
        case 'helpText':     return !!field.text;
        case 'select':       return !!(field.options || field.ref_model);
        case 'autocomplete': return !!field.ref_model;
        case 'formbase':     return true;   // tự ẩn khi chưa có __fields
        default:             return false;
    }
};

// --- Main Component ---
const PropertiesBar = ({ flowId, selectedNode, onNodeDataChange, onClose, isReadOnly, nodeTypeMetadata }) => {
    const [formData, setFormData] = useState({});
    const [error, setError] = useState("");
    const context = useContext(Context);

    useEffect(() => {
        setError("");
        if ((selectedNode || {}).data) {
            const { status, nodeState, outputData, error: nodeError, inputPayload, ...configData } = selectedNode.data;
            setFormData({...configData });
        } else {
            setFormData({});
        }
    }, [selectedNode]);

    if (!selectedNode) return null;

    const { id, type } = selectedNode;
    // Metadata do FlowEditor truyền vào (đã trộn client + máy chủ). Bản tĩnh chỉ là dự phòng.
    const metadataMap = nodeTypeMetadata || staticNodeTypeMetadata;
    const fields = metadataMap[type] || [];

    const handleInputChange = (field, newValue = {}, ref = undefined) => {
        setError("");
        // Chỉ đọc (flow đang chạy): cho xem cấu hình nhưng không nhận thay đổi.
        if (isReadOnly) return;
        if ((field || {}).onChange) {
            field.onChange(context, flowId, formData, newValue, ref, fields);
        }
        setFormData(prevData => ({ ...prevData, ...newValue }));
    };

    const handleRuleInputChange = (listFieldName, ruleIndex, ruleFieldName, data) => {
        setError("");
        if (isReadOnly) return;
        setFormData(prevData => {
            const newList = [...(prevData[listFieldName] || [])];
            if (newList[ruleIndex]) {
                newList[ruleIndex] = { ...newList[ruleIndex], ...data };
            }
            return { ...prevData, [listFieldName]: newList };
        });
    };

    const handleAddRule = useCallback((listFieldName, ruleFields) => {
        if (isReadOnly) return;
        const newRule = ruleFields.reduce((acc, field) => {
            acc[field.name] = field.default !== undefined ? field.default : '';
            return acc;
        }, {});

        setFormData(prevData => ({
            ...prevData,
            [listFieldName]: [...(prevData[listFieldName] || []), newRule]
        }));
    }, [isReadOnly]);

    const handleRemoveRule = useCallback((listFieldName, ruleIndex) => {
        if (isReadOnly) return;
        setFormData(prevData => ({
            ...prevData,
            [listFieldName]: (prevData[listFieldName] || []).filter((_, index) => index !== ruleIndex)
        }));
    }, [isReadOnly]);

    const handleSave = async () => {
        /*if (!formData.name) {
            return setError("Tên node không được trống");
        }*/
        try {
            await onNodeDataChange(id, formData);
            if (onClose) onClose();
        } catch (e) {
            setError(e.message || e.error || e);
        }
    };

    const shouldShowField = (field) => {
        if (!field.showIf) return true;
        if (typeof field.showIf === "function") return field.showIf(field, formData);
        
        const { field: dependencyField, value: dependencyValue } = field.showIf;
        const currentDependencyValue = formData[dependencyField];

        return Array.isArray(dependencyValue)
            ? dependencyValue.includes(currentDependencyValue)
            : currentDependencyValue === dependencyValue;
    };

    const renderFieldInput = (field, value, onChangeCallback) => {
        return (
            <>
                {(field.type === 'text' || field.type === 'password') && (
                    <TextField
                        field={{ md: 12, lg: 12 }}
                        required={field.required}
                        variant="outlined"
                        value={value || ""}
                        fullWidth
                        readOnly={field.readOnly}
                        InputProps={{ readOnly: field.readOnly, type: field.type }}
                        onChange={(event) => onChangeCallback(field, { [field.name]: event.target.value })}
                    />
                )}
                {(field.type === 'helpText' && field.text) && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                        {field.text}
                    </Typography>
                )}
                {field.type === 'textarea' && (
                    <TextField
                        field={{ md: 12, lg: 12 }}
                        required={field.required}
                        variant="outlined"
                        value={value || ""}
                        multiline
                        fullWidth
                        readOnly={field.readOnly}
                        InputProps={{ readOnly: field.readOnly, type: field.type }}
                        onChange={(event) => onChangeCallback(field, { [field.name]: event.target.value })}
                    />
                )}
                {field.type === 'code' && (
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                        <CodeEditor
                            data={value || ""}
                            disabled={field.readOnly}
                            onChange={(event, _data) => onChangeCallback(field, { [field.name]: _data })}
                        />
                    </Box>
                )}
                {field.type === 'number' && (
                    <NumberField
                        field={{ md: 12, lg: 12 }}
                        required={field.required}
                        variant="outlined"
                        value={value || 0}
                        fullWidth
                        readOnly={field.readOnly}
                        InputProps={{ readOnly: field.readOnly }}
                        onChange={(event) => onChangeCallback(field, { [field.name]: Number(event.target.value || 0) })}
                    />
                )}
                {field.type === 'select' && (field.options || field.ref_model) && (
                    <Picker
                        fullWidth
                        dataType={"string"}
                        required={field.required}
                        value={value}
                        valueField={field.ref_field || "value"}
                        labelField={field.ref_label || "label"}
                        disabled={field.disabled}
                        isRadio={field.isRadio}
                        readOnly={field.readOnly}
                        condition={(formData.__fields || {})[field.name]?.condition || field.condition}
                        variant={"outlined"}
                        items={async (query, options) => {
                            if (Array.isArray(field.options)) return field.options;
                            return getItemsForPicker(context, context.appInfo?._id, field, query, options);
                        }}
                        onValueChanged={(val, label) => {
                            let data = { [field.name]: val };
                            if (field.ref_label_as || field.ref_label) {
                                data[field.ref_label_as || field.ref_label] = label;
                            }
                            onChangeCallback(field, data);
                        }}
                    />
                )}
                {field.type === 'autocomplete' && field.ref_model && (
                    <AutoCompleteInput
                        fullWidth
                        dataType={"string"}
                        required={field.required}
                        value={value}
                        model={field.ref_model}
                        multiple={field.multiple}
                        valueField={field.ref_field || "value"}
                        labelField={field.ref_label || "label"}
                        disabled={field.disabled}
                        isRadio={field.isRadio}
                        readOnly={field.readOnly}
                        condition={(formData.__fields || {})[field.name]?.condition || field.condition}
                        variant={"outlined"}
                        getOptionLabel={option => {
                            let label = option[field.ref_label || field.ref_field] || "";
                            let val = (option[field.ref_field] || "").toString();
                            return field.ref_model === "account" ? `${val}` : `${label}`;
                        }}
                        items={async (query, options = { cache: false, limit: 20, online: true }) => {
                            return getItemsForCompoleteInput(context, context.appInfo?._id, field, query, options);
                        }}
                        onValueChanged={(val, label, ref_data) => {
                            let data = { [field.name]: val };
                            if (!field.multiple && (field.ref_label_as || field.ref_label)) {
                                data[field.ref_label_as || field.ref_label] = label;
                            }
                            onChangeCallback(field, data, ref_data);
                        }}
                    />
                )}
                {field.type === 'checkbox' && (
                    <Checkbox
                        label={field.label}
                        disabled={field.readOnly}
                        readOnly={field.readOnly}
                        value={value}
                        onChange={(event) => onChangeCallback(field, { [field.name]: event.target.checked })}
                    />
                )}
                {!isRenderableField(field) && (
                    <TextField
                        field={{ md: 12, lg: 12 }}
                        required={field.required}
                        variant="outlined"
                        value={typeof value === 'object' && value !== null ? JSON.stringify(value) : (value ?? "")}
                        multiline={field.type === 'object' || field.type === 'json'}
                        fullWidth
                        readOnly={field.readOnly}
                        helperText={`Kiểu trường '${field.type || 'không rõ'}' chưa có giao diện riêng — nhập dạng văn bản.`}
                        InputProps={{ readOnly: field.readOnly, type: 'text' }}
                        onChange={(event) => onChangeCallback(field, { [field.name]: event.target.value })}
                    />
                )}
                {field.type === 'formbase' && (formData.__fields || {})[field.name]?.code && (
                    <Box sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <FormBase
                            style={{ margin: 0 }}
                            code={(formData.__fields || {})[field.name]?.code}
                            mediaQueryMatches={true}
                            showFieldName={true}
                            handleValueChanged={_data => onChangeCallback(field, { [field.name]: _data })}
                            data={formData[field.name] || {}}
                        />
                    </Box>
                )}
            </>
        );
    };

    return (
        <Box className="PropertiesBar" sx={{ width: '100%', maxWidth: 500, bgcolor: 'background.paper', height: '100%', display: 'flex', flexDirection: 'column' }}>
            
            {/* 1. FIXED HEADER */}
            <Box sx={{ flexShrink: 0 }}>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
                        <Typography variant="h6" component="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }} noWrap>
                            {formData.name || id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace' }}>
                            ID: {id}
                        </Typography>
                    </Box>
                    {onClose && (
                        <IconButton onClick={onClose} size="small" edge="end">
                            <CloseIcon />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* 2. SCROLLABLE CONTENT BODY */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
                <Stack spacing={2.5}>
                    {isReadOnly && (
                        <Alert severity="info" sx={{ py: 0, fontSize: 12 }}>
                            Quy trình đang chạy — chỉ xem cấu hình. Dừng quy trình để chỉnh sửa.
                        </Alert>
                    )}
                    {/* Node Name Field */}
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                            Tên node <Box component="span" sx={{ color: 'error.main' }}></Box>:
                        </Typography>
                        <TextField
                            field={{ md: 12, lg: 12 }}
                            required={false}
                            value={formData.name || ""}
                            variant="outlined"
                            fullWidth
                            readOnly={false}
                            InputProps={{ readOnly: false, type: "text" }}
                            onChange={(event) => handleInputChange({ name: "name" }, { name: event.target.value })}
                        />
                    </Box>

                    {/* Dynamic Metadata Fields */}
                    {fields.filter(field => shouldShowField(field)).map((field) => (
                        <Box key={field.key || field.name}>
                            {field.type !== 'list' && (
                                <>
                                    {field.type !== 'checkbox' && field.type !== 'helpText' && (
                                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                            {field.label}
                                            {field.required && <Box component="span" sx={{ color: 'error.main' }}> (*)</Box>}
                                        </Typography>
                                    )}
                                    {field.helper && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                            {field.helper}
                                        </Typography>
                                    )}
                                    {renderFieldInput(field, formData[field.name], handleInputChange)}
                                </>
                            )}

                            {/* Rule List Field */}
                            {field.type === 'list' && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>{field.label}</Typography>
                                    {field.helper && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                            {field.helper}
                                        </Typography>
                                    )}
                                    
                                    <Stack spacing={2} sx={{ mb: 2 }}>
                                        {(formData[field.name] || []).map((rule, index) => (
                                            <Card variant="outlined" key={index} sx={{ bgcolor: 'grey.25' }}>
                                                <Box sx={{ px: 2, py: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'grey.100' }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Quy tắc #{index + 1}</Typography>
                                                    <Button 
                                                        size="small" 
                                                        color="error" 
                                                        startIcon={<DeleteIcon />} 
                                                        disabled={isReadOnly}
                                                        onClick={() => handleRemoveRule(field.name, index)}
                                                    >
                                                        Xóa
                                                    </Button>
                                                </Box>
                                                <Divider />
                                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                    <Stack spacing={2}>
                                                        {(field.fields || []).map(ruleField => (
                                                            <Box key={ruleField.name}>
                                                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                                                    {ruleField.label}
                                                                    {ruleField.required && <Box component="span" sx={{ color: 'error.main' }}> (*)</Box>}
                                                                </Typography>
                                                                {ruleField.helper && (
                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                                        {ruleField.helper}
                                                                    </Typography>
                                                                )}
                                                                {renderFieldInput(
                                                                    { ...ruleField, id: `${field.name}-${index}-${ruleField.name}`, name: ruleField.name },
                                                                    rule[ruleField.name],
                                                                    (f, data) => handleRuleInputChange(field.name, index, ruleField.name, data)
                                                                )}
                                                            </Box>
                                                        ))}
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Stack>
                                    
                                    <Button 
                                        variant="outlined" 
                                        size="small" 
                                        startIcon={<AddIcon />}
                                        disabled={isReadOnly}
                                        onClick={() => handleAddRule(field.name, field.fields || [])}
                                        fullWidth
                                    >
                                        Thêm quy tắc
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    ))}

                    {/* Node Disabling Option */}
                    <Box>
                        <Checkbox
                            label={"Vô hiệu hoá node này"}
                            disabled={false}
                            readOnly={false}
                            value={formData.disabled}
                            onChange={(event) => handleInputChange({ name: "disabled" }, { disabled: event.target.checked })}
                        />
                    </Box>
                </Stack>
            </Box>

            {/* 3. FIXED FOOTER ACTION BAR */}
            <Box sx={{ flexShrink: 0, p: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
                <Stack spacing={1.5}>
                    {error && (
                        <Alert severity="error" variant="filled" sx={{ width: '100%', py: 0.5 }}>
                            {error}
                        </Alert>
                    )}
                    <Button 
                        variant="contained" 
                        color="primary" 
                        size="large" 
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        fullWidth
                        disabled={isReadOnly}
                    >
                        Lưu thay đổi
                    </Button>
                </Stack>
            </Box>
        </Box>
    );
};

export default PropertiesBar;